// services/auth.ts
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import axiosInstance from './axiosCreator';
import { getAuthClient, type AuthClientError } from './authClient';
import {
  APPLY_SIGNIN_CONTEXT_PATH,
  IMPERSONATE_SESSION_PATH,
  STOP_IMPERSONATING_PATH,
} from './authContracts';
import { withDefaultAuthAttribution } from '../utils/attribution';
import { getLtiIntentFetchOptions } from './lti';
import { User, UpdateUserInfoRequest } from '../types/User';
import { persistor, store } from '../store/store';
import {
  authFailed,
  authLoading,
  authSecondFactorRequired,
  authSuccess,
  impersonationStarted,
  impersonationStopped,
  logout,
  readStoredImpersonation,
  sessionIdentityChanged,
  type ImpersonationRecord,
} from '../slices/auth/authSlice';
import {
  broadcastSessionChange,
  subscribeToSessionChanges,
  type SessionChangeEvent,
} from './sessionSync';
import { getSignupPlatform, SignupPlatform } from '../utils/signupPlatform';
import {
  getPasswordPolicyError,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
} from '../utils/validEmail';
import {
  userFailed,
  userLoading,
  userReceived,
  userUpdateReceived,
} from '../slices/user/userSlice';
import { fetchExams } from './exams';
import { isLegacyExchangePending } from './legacyExchange';
import { loadActiveDiscount } from '../slices/discount/discountSlice';
import { clearGuestIdentity } from '../pages/Games/SchoolLeaderboard/guestIdentity';
import { setAllMessageStates } from '../slices/messageState/messageStateSlice';
import { resetPosthog } from '../utils/posthogClient';
import { unregisterPushDeviceForSignOut } from './pushNotifications';
import {
  SIGN_OUT_UNCONFIRMED_CODE,
  SIGN_OUT_UNCONFIRMED_MESSAGE,
} from './signOutUnconfirmed';

/**
 * Client context a sign-in or sign-up carries to the backend lifecycle hooks
 * (ORA-987: Better Auth's `additionalData`).
 */
export interface SignInContext {
  // ORA-92: an unlock cohort + the edu entered on the /unlock page, carried
  // through login so an existing account still lands the school discount.
  cohort?: string | null;
  unlockEmail?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  landingPath?: string;
  inferred?: boolean;
}

export interface SignUpContext extends SignInContext {
  timezone?: string;
  // ORA-900: the client the account is created on. Filled in by register()
  // itself; never sent on login.
  platform?: SignupPlatform;
  referralId?: string | null;
  buyIntent?: string;
  ambassador?: boolean;
}

interface UserCredentials extends SignInContext {
  email: string;
  password: string;
}

interface RegisterInfo extends SignUpContext {
  email: string;
  password: string;
  isMobileApp?: boolean;
}

/** What every sign-in entry point resolves to; never rejects. */
export interface AuthResult {
  success: boolean;
  data?: { userId: string };
  error?: string;
  /** The Better Auth error code, when there is one. */
  code?: string;
  /** The HTTP status of a refusal, when there is one. */
  status?: number;
  /** The password was right; the account needs its second factor next. */
  twoFactorRequired?: boolean;
}

interface ClaimMarketingOfferResponse {
  marketingOfferKey: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export const ACCOUNT_EXISTS_MESSAGE =
  'An account with this email already exists. Please sign in instead.';
export const INVALID_CREDENTIALS_MESSAGE = 'Invalid email or password';
const PASSWORD_TOO_SHORT_MESSAGE = `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
const PASSWORD_TOO_LONG_MESSAGE = `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`;
export const RATE_LIMITED_MESSAGE =
  'Too many attempts. Please wait a minute and try again.';

/**
 * The backend's per-account sign-in throttle (ORA-1089): 429
 * `TOO_MANY_ATTEMPTS` on `/api/auth/sign-in/email`. Ora's own copy, never the
 * server's text.
 */
export const TOO_MANY_ATTEMPTS_CODE = 'TOO_MANY_ATTEMPTS';
export const TOO_MANY_SIGN_IN_ATTEMPTS_MESSAGE =
  'Too many sign-in attempts. Please wait a few minutes and try again, or reset your password.';

/**
 * Demo accounts may not take a password or an email of their own: the backend
 * refuses `/auth/set-password` and `/auth/changeEmail` with 403
 * `DEMO_ACCOUNT` (ORA-1089).
 */
export const DEMO_ACCOUNT_CODE = 'DEMO_ACCOUNT';
export const DEMO_ACCOUNT_MESSAGE =
  'Create a free account first to set a password or email.';

/**
 * A Better Auth error as the sentence a student should read. Known codes get
 * Ora's own copy (the account-exists wording is what the unlock and webinar
 * flows match on); anything else keeps the server's message.
 */
export const describeAuthError = (
  error: AuthClientError | null | undefined,
  fallback: string
): string => {
  const code = error?.code ?? '';
  if (code.startsWith('USER_ALREADY_EXISTS')) return ACCOUNT_EXISTS_MESSAGE;
  if (code === 'INVALID_EMAIL_OR_PASSWORD') return INVALID_CREDENTIALS_MESSAGE;
  if (code === 'PASSWORD_TOO_SHORT') return PASSWORD_TOO_SHORT_MESSAGE;
  if (code === 'PASSWORD_TOO_LONG') return PASSWORD_TOO_LONG_MESSAGE;
  if (code === TOO_MANY_ATTEMPTS_CODE) return TOO_MANY_SIGN_IN_ATTEMPTS_MESSAGE;
  if (error?.status === 429) return RATE_LIMITED_MESSAGE;
  return error?.message || fallback;
};

/** Drops empty values so only real context reaches the backend. */
const compactContext = (
  context: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(context).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

/**
 * A sign-in that stops at the 2FA page creates its session there, where the
 * backend's sign-in hook no longer sees the context. It is parked here and
 * applied once the second factor succeeds.
 */
const PENDING_SIGNIN_CONTEXT_KEY = 'ora-pending-signin-context';

export const parkSignInContext = (context: Record<string, unknown>) => {
  try {
    if (Object.keys(context).length === 0) {
      sessionStorage.removeItem(PENDING_SIGNIN_CONTEXT_KEY);
      return;
    }
    sessionStorage.setItem(PENDING_SIGNIN_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // Storage unavailable: the context is best-effort.
  }
};

/** Drops a parked context that no second factor will claim. */
export const discardParkedSignInContext = (): void => {
  try {
    sessionStorage.removeItem(PENDING_SIGNIN_CONTEXT_KEY);
  } catch {
    // Nothing to drop.
  }
};

const takeParkedSignInContext = (): Record<string, unknown> | null => {
  try {
    const stored = sessionStorage.getItem(PENDING_SIGNIN_CONTEXT_KEY);
    sessionStorage.removeItem(PENDING_SIGNIN_CONTEXT_KEY);
    const parsed = stored ? (JSON.parse(stored) as unknown) : null;
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

export const SIGN_IN_CONTEXT_FAILED_MESSAGE =
  "You're signed in, but we couldn't link your school or offer. Open the link again to retry.";

/** No response at all (offline, dropped connection): worth one retry. */
const isNetworkError = (error: unknown): boolean =>
  axios.isAxiosError(error) && !error.response;

/**
 * Applies sign-in context (unlock cohort, UTM attribution) to the session's
 * user after a sign-in that could not carry it, retrying once on a network
 * error. Resolves false when it finally failed; the sign-in itself has
 * already succeeded.
 */
export const applySignInContext = async (
  context: Record<string, unknown>
): Promise<boolean> => {
  const payload = compactContext(context);
  if (Object.keys(payload).length === 0) return true;

  for (let attempt = 1; ; attempt += 1) {
    try {
      await axiosInstance.post(APPLY_SIGNIN_CONTEXT_PATH, payload);
      return true;
    } catch (error) {
      if (attempt === 1 && isNetworkError(error)) continue;
      console.error('Failed to apply sign-in context:', error);
      return false;
    }
  }
};

/**
 * Set while a Log Out's revoke has not been confirmed (ORA-1089): the session
 * cookie may still be live, and the next person on this browser must not be
 * signed back in with it. Cleared by a confirmed revoke or a new sign-in.
 */
export const SIGN_OUT_PENDING_KEY = 'ora-signout-pending';

const isSignOutPending = (): boolean => {
  try {
    return localStorage.getItem(SIGN_OUT_PENDING_KEY) === '1';
  } catch {
    return false;
  }
};

const setSignOutPending = (isPending: boolean): void => {
  try {
    if (isPending) localStorage.setItem(SIGN_OUT_PENDING_KEY, '1');
    else localStorage.removeItem(SIGN_OUT_PENDING_KEY);
  } catch {
    // Storage unavailable: nothing to remember the sign-out with.
  }
};

/**
 * The session now belongs to another account: the previous account's
 * analytics identity ends, and every user-scoped slice starts over
 * (ORA-1089: PostHog is reset here, outside the reducer).
 */
const changeSessionIdentity = (identity: {
  userId: string;
  impersonatedBy: string | null;
}): void => {
  resetPosthog();
  store.dispatch(sessionIdentityChanged(identity));
};

/** Store updates shared by every way a session can start. */
const completeSignIn = (userId: string): void => {
  // The cookie is now this sign-in's: no earlier Log Out is owed on it.
  setSignOutPending(false);
  // The server just named this account: no boot identity check needed.
  // Marked first, so no render sees a signed-in store still unchecked (the
  // route guard would swap the page for its spinner).
  markSessionIdentityChecked();
  store.dispatch(authSuccess({ userId }));
  broadcastSessionChange('signed-in', userId);

  // Write the signed-in state now rather than on redux-persist's throttle: a
  // reload in that window would otherwise boot from the persisted
  // `status: 'loading'` of the sign-in that just finished.
  void persistor.flush().catch(() => undefined);

  // A signed-in user plays events under their account, not a leftover
  // /play guest persona.
  clearGuestIdentity();

  // Immediately refresh discount eligibility after auth
  store.dispatch(loadActiveDiscount());
};

/**
 * A sign-in whose context the backend hook did not see (web 2FA, passkey):
 * the context lands before the store signs in, so the discount eligibility
 * `completeSignIn` loads (and the page the user lands on) sees the unlock
 * cohort. A context that finally fails is a non-blocking toast.
 */
const completeSignInWithContext = async (
  userId: string,
  context: Record<string, unknown> | null
): Promise<void> => {
  const isApplied = context ? await applySignInContext(context) : true;
  if (!isApplied) {
    enqueueSnackbar(SIGN_IN_CONTEXT_FAILED_MESSAGE, { variant: 'warning' });
  }
  completeSignIn(userId);
};

const failSignIn = (
  error: AuthClientError | null | undefined,
  fallback: string
): AuthResult => {
  const message = describeAuthError(error, fallback);
  // Without this the status stays 'loading', which ProtectedRoute renders as
  // a permanent spinner (and it survives a reload: the slice is persisted).
  store.dispatch(authFailed(message));
  return { success: false, error: message, code: error?.code };
};

/** Email + password sign-in. Resolves `twoFactorRequired` for 2FA accounts. */
export const login = async (
  credentials: UserCredentials
): Promise<AuthResult> => {
  const { email, password, ...context } = credentials;
  const additionalData = compactContext(withDefaultAuthAttribution(context));

  store.dispatch(authLoading());

  // ORA-1046: a sign-in that completes an LTI linking intent names it.
  const fetchOptions = getLtiIntentFetchOptions();

  try {
    // An earlier Log Out's revoke goes first (ORA-1089).
    if (!(await startSignIn())) {
      return failSignIn(
        {
          code: SIGN_OUT_UNCONFIRMED_CODE,
          message: SIGN_OUT_UNCONFIRMED_MESSAGE,
        },
        SIGN_OUT_UNCONFIRMED_MESSAGE
      );
    }

    const { data, error } = await getAuthClient().signIn.email({
      email,
      password,
      // Read by the backend's sign-in hook (src/auth/lifecycleHooks.ts).
      ...{ additionalData },
      ...(fetchOptions ? { fetchOptions } : {}),
    });

    if (error || !data) return failSignIn(error, 'Login failed');

    if ('twoFactorRedirect' in data && data.twoFactorRedirect) {
      parkSignInContext(additionalData);
      store.dispatch(authSecondFactorRequired());
      return { success: false, twoFactorRequired: true };
    }

    if (!('user' in data) || !data.user?.id) {
      return failSignIn(null, 'Login failed');
    }

    completeSignIn(data.user.id);
    return { success: true, data: { userId: data.user.id } };
  } catch {
    return failSignIn(null, 'Login failed');
  }
};

/**
 * Creates an email + password account. Better Auth signs the new account in
 * on the same request, so a successful register is already a session.
 */
export const register = async (info: RegisterInfo): Promise<AuthResult> => {
  const policyError = getPasswordPolicyError(info.password);
  if (policyError) {
    return { success: false, error: policyError, code: 'PASSWORD_POLICY' };
  }

  const { email, password, ...context } = info;
  // `isMobileApp` was a legacy body field; `platform` replaced it (ORA-900).
  delete context.isMobileApp;

  const additionalData = compactContext(
    withDefaultAuthAttribution({ ...context, platform: getSignupPlatform() })
  );

  try {
    // An earlier Log Out's revoke goes first (ORA-1089).
    if (!(await startSignIn())) {
      return {
        success: false,
        error: SIGN_OUT_UNCONFIRMED_MESSAGE,
        code: SIGN_OUT_UNCONFIRMED_CODE,
      };
    }

    const { data, error } = await getAuthClient().signUp.email({
      email,
      password,
      name: '',
      // Read by the backend's user-create hook (src/auth/lifecycleHooks.ts).
      ...{ additionalData },
    });

    if (error || !data?.user?.id) {
      return {
        success: false,
        error: describeAuthError(error, 'Could not create your account.'),
        code: error?.code,
      };
    }

    completeSignIn(data.user.id);
    return { success: true, data: { userId: data.user.id } };
  } catch {
    return { success: false, error: 'Could not create your account.' };
  }
};

export const PASSKEY_SIGN_IN_FAILED_MESSAGE =
  'Passkey sign-in failed. Please try again.';

/** Sign-in with a passkey (web only). Conditional UI when `autoFill`. */
export const loginWithPasskey = async (
  context: SignInContext = {},
  options: { autoFill?: boolean } = {}
): Promise<AuthResult> => {
  // ORA-1046: a sign-in that completes an LTI linking intent names it.
  const fetchOptions = getLtiIntentFetchOptions();

  try {
    // An earlier Log Out's revoke goes first (ORA-1089).
    if (!(await startSignIn())) {
      return {
        success: false,
        error: SIGN_OUT_UNCONFIRMED_MESSAGE,
        code: SIGN_OUT_UNCONFIRMED_CODE,
      };
    }

    const result = await getAuthClient().signIn.passkey({
      autoFill: options.autoFill,
      ...(fetchOptions ? { fetchOptions } : {}),
    });

    const error = result?.error as AuthClientError | null | undefined;
    const data = result?.data as { user?: { id?: string } } | null | undefined;

    if (error || !data) {
      return {
        success: false,
        // Better Auth's AUTH_CANCELLED ("Auth cancelled") covers any
        // non-dismissal failure; a dismissed dialog has its own code, which
        // callers drop silently (`isPasskeyCancellation`).
        error:
          error?.code === 'AUTH_CANCELLED'
            ? PASSKEY_SIGN_IN_FAILED_MESSAGE
            : describeAuthError(error, PASSKEY_SIGN_IN_FAILED_MESSAGE),
        code: error?.code,
      };
    }

    const userId = data.user?.id ?? (await establishSessionFromCookie());
    if (!userId) return { success: false, error: 'Passkey sign-in failed.' };

    await completeSignInWithContext(
      userId,
      withDefaultAuthAttribution({ ...context })
    );

    return { success: true, data: { userId } };
  } catch {
    return { success: false, error: 'Passkey sign-in failed.' };
  }
};

/**
 * Completes a sign-in on the 2FA page: a TOTP code or a backup code, against
 * the two-factor cookie the password step set.
 */
export const verifySecondFactor = async ({
  code,
  method,
  trustDevice,
}: {
  code: string;
  method: 'totp' | 'backup';
  trustDevice: boolean;
}): Promise<AuthResult> => {
  // ORA-1046: the proof of an LTI linking intent is recorded on this call
  // when the password or passkey step was held for 2FA (social sign-in is
  // not linking proof; the backend ignores the header after one).
  const fetchOptions = getLtiIntentFetchOptions();
  const body = { code, trustDevice, ...(fetchOptions ? { fetchOptions } : {}) };

  try {
    const twoFactor = getAuthClient().twoFactor;

    const { data, error } =
      method === 'totp'
        ? await twoFactor.verifyTotp(body)
        : await twoFactor.verifyBackupCode(body);

    const userId =
      (data as { user?: { id?: string } } | null)?.user?.id ?? null;

    if (error || !userId) {
      return {
        success: false,
        error: describeAuthError(error, 'That code did not work.'),
        code: error?.code,
        status: error?.status,
      };
    }

    await completeSignInWithContext(userId, takeParkedSignInContext());
    return { success: true, data: { userId } };
  } catch {
    return { success: false, error: 'That code did not work.' };
  }
};

type CookieSessionLookup =
  | { status: 'session'; userId: string }
  /** The server answered: the cookie is no session (or an anonymous one). */
  | { status: 'none' }
  /** The server could not be asked (offline, a cold instance, 5xx, 429). */
  | { status: 'unknown' };

/** Whose session the cookie is, as far as the server could say. */
const lookupCookieSession = async (): Promise<CookieSessionLookup> => {
  let response: ProfileResponse;
  try {
    response = await requestProfile(false);
  } catch (error) {
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined;

    return { status: status === 401 ? 'none' : 'unknown' };
  }

  const profile = response.success ? response.data : undefined;
  if (!profile?.id || profile.isAnon) return { status: 'none' };

  return { status: 'session', userId: profile.id };
};

/**
 * Turns a session cookie the backend already set (a social sign-in redirect,
 * a native one-time token) into the signed-in store state. Resolves to the
 * user id, or null when there is no usable session.
 */
export const establishSessionFromCookie = async (): Promise<string | null> => {
  if (await holdForPendingSignOut()) return null;
  const session = await lookupCookieSession();
  return session.status === 'session' ? session.userId : null;
};

/** Store the session found by `establishSessionFromCookie`, if any. */
export const resumeSessionFromCookie = async (): Promise<string | null> => {
  const userId = await establishSessionFromCookie();
  if (userId) completeSignIn(userId);
  return userId;
};

/**
 * After the boot's legacy exchange set a session cookie: store the account
 * it belongs to, and reload the profile when the persisted one is someone
 * else's (an admin who was mid-impersonation trades their own token). No
 * session after all signs the browser out.
 */
export const syncSessionAfterLegacyExchange = async (): Promise<void> => {
  if (await holdForPendingSignOut()) return;
  const persistedProfileId = store.getState().user.profile?.id ?? null;
  const session = await lookupCookieSession();

  // The server could not be asked: keep the persisted state for the route
  // guard's identity check to settle. The legacy tokens are spent, so a
  // sign-out here would be for good (ORA-1004). Not for an admin who was
  // mid-impersonation, though: the traded cookie is their own, and acting
  // on the student's persisted state with it would be wrong.
  if (session.status === 'unknown') {
    if (readStoredImpersonation()?.impersonatedBy) store.dispatch(logout());
    return;
  }

  if (session.status === 'none') {
    if (store.getState().auth.isAuthenticated) store.dispatch(logout());
    return;
  }

  const { userId } = session;
  completeSignIn(userId);

  // Another account's leftovers: start the user-scoped state over; the
  // route guard then loads this account's profile and exams.
  if (persistedProfileId && persistedProfileId !== userId) {
    changeSessionIdentity({
      userId,
      impersonatedBy: readStoredImpersonation()?.impersonatedBy ?? null,
    });
  }
};

/** How long Log Out waits for the revoke before signing this browser out. */
export const SIGN_OUT_REVOKE_TIMEOUT_MS = 3_000;

/**
 * Revokes the session cookie on the backend. Resolves true once the backend
 * confirmed it; false on a refusal, an error or after the wait.
 */
const revokeSession = async (): Promise<boolean> => {
  // A revoke that outlives the wait is abandoned, not left to land later:
  // its response clears the session cookie, which by then may be a new
  // sign-in's.
  const revoke = new AbortController();
  let timer: number | undefined;
  try {
    const result = await Promise.race([
      getAuthClient().signOut({ fetchOptions: { signal: revoke.signal } }),
      new Promise<never>((_, reject) => {
        timer = window.setTimeout(() => {
          revoke.abort();
          reject(new Error('Session revoke timed out'));
        }, SIGN_OUT_REVOKE_TIMEOUT_MS);
      }),
    ]);

    if (result?.error) {
      console.error('Failed to revoke the session:', result.error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Failed to revoke the session:', error);
    return false;
  } finally {
    window.clearTimeout(timer);
  }
};

let pendingSignOutRetry: Promise<boolean> | null = null;

/**
 * Retries the revoke of a Log Out that never landed. Resolves true when no
 * sign-out is owed any more (none was, or the retry landed).
 */
const settlePendingSignOut = (): Promise<boolean> => {
  if (!isSignOutPending()) return Promise.resolve(true);

  pendingSignOutRetry ??= revokeSession()
    .then((isRevoked) => {
      if (isRevoked) setSignOutPending(false);
      return isRevoked;
    })
    .finally(() => {
      pendingSignOutRetry = null;
    });

  return pendingSignOutRetry;
};

/**
 * Every sign-in start (password, sign-up, passkey, Google/Apple, native)
 * first settles a Log Out still owed on this browser (ORA-1089). Resolves
 * false when its revoke could not be confirmed: the sign-in must not start,
 * or a cancelled or failed attempt would leave the previous person's live
 * cookie with nothing guarding it. The flag clears only on a confirmed
 * revoke, so a returning callback's new session is never revoked as the old.
 */
export const startSignIn = (): Promise<boolean> => settlePendingSignOut();

/**
 * Called before a cookie session is adopted (boot, resume, identity sync).
 * With a Log Out still owed, its revoke is retried and nothing is adopted
 * either way: a revoked cookie is gone, and an unrevoked one belongs to
 * whoever logged out. The browser stays signed out. Resolves true when the
 * caller must not adopt.
 */
export const holdForPendingSignOut = async (): Promise<boolean> => {
  if (!isSignOutPending()) return false;
  await settlePendingSignOut();
  if (store.getState().auth.isAuthenticated) store.dispatch(logout());
  return true;
};

/**
 * Signs out: detaches this device's push token, revokes the session cookie
 * on the backend, then clears the store. A failed or slow revoke (offline, a
 * hung backend) still signs this browser out: the tap must not look dead
 * (ORA-1004). It is remembered and retried before any later cookie session
 * is adopted (ORA-1089).
 */
export const signOut = async (): Promise<void> => {
  // Needs the session the revoke ends; bounded, never throws (ORA-1089).
  await unregisterPushDeviceForSignOut();

  setSignOutPending(true);
  if (await revokeSession()) setSignOutPending(false);

  // Announced before the store clears, so the clear also removes the
  // storage-event fallback's copy of it.
  broadcastSessionChange('signed-out', null);
  store.dispatch(logout());
};

/** The impersonation in progress, read live: the cookie it rides on is shared
 * by every tab, so another tab may have started or stopped it. */
export const getActiveImpersonation = (): ImpersonationRecord | null =>
  readStoredImpersonation();

/** True for a reset link minted by the legacy `/auth/forgotPassword` (a
 * three-segment JWT); Better Auth reset tokens are opaque. */
export const isLegacyResetToken = (token: string): boolean =>
  token.split('.').length === 3;

/**
 * Sends a reset email. The backend answers the same whether or not the
 * address has an account, so this only fails on transport errors.
 */
export const forgotPassword = async (
  email: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await getAuthClient().requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset`,
    });

    return error
      ? {
          success: false,
          error: describeAuthError(error, 'Could not send the reset email.'),
        }
      : { success: true };
  } catch {
    return { success: false, error: 'Could not send the reset email.' };
  }
};

/**
 * Sets a new password from a reset link. Links emailed before the cutover
 * carry a legacy JWT and still go to the legacy route.
 */
export const resetPassword = async (
  resetToken: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  const policyError = getPasswordPolicyError(newPassword);
  if (policyError) return { success: false, error: policyError };

  if (isLegacyResetToken(resetToken)) {
    try {
      const response = await axiosInstance.post<ApiResponse<unknown>>(
        'auth/resetPassword',
        { resetToken, newPassword }
      );

      return response.data.success
        ? { success: true }
        : {
            success: false,
            error: response.data.error || RESET_FAILED_MESSAGE,
          };
    } catch (error) {
      const message = axios.isAxiosError<ApiResponse<unknown>>(error)
        ? error.response?.data?.error
        : undefined;

      return { success: false, error: message || RESET_FAILED_MESSAGE };
    }
  }

  try {
    const { error } = await getAuthClient().resetPassword({
      newPassword,
      token: resetToken,
    });

    return error
      ? { success: false, error: describeResetError(error) }
      : { success: true };
  } catch {
    return { success: false, error: RESET_FAILED_MESSAGE };
  }
};

const RESET_FAILED_MESSAGE =
  'This reset link is invalid or has expired. Please request a new one.';

const describeResetError = (error: AuthClientError): string =>
  error.code === 'INVALID_TOKEN' || error.status === 400
    ? RESET_FAILED_MESSAGE
    : describeAuthError(error, RESET_FAILED_MESSAGE);

/**
 * Changes the password and signs every other device out. The session cookie
 * is reissued on the same response, so this browser stays signed in.
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<{ success?: boolean; error?: string }> => {
  const policyError = getPasswordPolicyError(newPassword);
  if (policyError) return { error: policyError };

  try {
    const { error } = await getAuthClient().changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: true,
    });

    if (error) {
      return {
        error:
          error.code === 'INVALID_PASSWORD'
            ? 'Current password is not correct.'
            : describeAuthError(error, 'Failed to change password'),
      };
    }
    return { success: true };
  } catch {
    return { error: 'Failed to change password' };
  }
};

// Change email (legacy route; authenticated by the session cookie)
export const changeEmail = async (newEmail: string, password: string) => {
  try {
    const response = await axiosInstance.post('auth/changeEmail', {
      newEmail: newEmail,
      password: password,
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to change email');
    }

    // Update user info in store if email change was successful
    if (response.data.data?.email) {
      // Refresh user info to get updated data
      await getUserInfo();
    }

    return response.data;
  } catch (error) {
    console.error('Failed to change email:', error);
    if (
      axios.isAxiosError(error) &&
      error.response?.data?.code === DEMO_ACCOUNT_CODE
    ) {
      return { error: DEMO_ACCOUNT_MESSAGE };
    }
    // Handle Axios errors specifically to extract the actual error message
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return {
        error: error.response.data.error,
      };
    }
    return {
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};

/** How long a fetched profile serves other boot callers (see below). */
export const PROFILE_REUSE_MS = 10_000;

type ProfileResponse = ApiResponse<User>;

let profileInFlight: Promise<ProfileResponse> | null = null;

let recentProfile: { response: ProfileResponse; fetchedAt: number } | null =
  null;

// Counts the profile writes that have landed. A read that was out when a
// write landed holds the profile from before it (see fetchProfile).
let profileWriteSeq = 0;

/**
 * `GET /auth/profile`, asked again whenever a write landed while it was out:
 * what came back is the profile from before the write, and handing it to
 * the caller would undo the write in the store. The response is kept for
 * reuse only once no write interleaved.
 */
const fetchProfile = async (): Promise<ProfileResponse> => {
  for (;;) {
    const writeSeq = profileWriteSeq;
    const { data } = await axiosInstance.get<ProfileResponse>('/auth/profile');

    if (writeSeq !== profileWriteSeq) continue;

    recentProfile =
      data.success && data.data
        ? { response: data, fetchedAt: Date.now() }
        : null;

    return data;
  }
};

/**
 * `GET /auth/profile`. Several callers want a fresh profile as the app boots
 * (the route guard, the session gates, the landing decision, the Account
 * page); with `reuseRecent` they share the request in flight, or a profile
 * of the signed-in account fetched in the last few seconds, so one boot
 * makes one request. Without it (after a write) the request always goes out.
 */
const requestProfile = (reuseRecent: boolean): Promise<ProfileResponse> => {
  if (reuseRecent) {
    // Safe to share even across a write: fetchProfile asks again.
    if (profileInFlight) return profileInFlight;

    const { userId, isAuthenticated } = store.getState().auth;
    if (
      recentProfile &&
      isAuthenticated &&
      recentProfile.response.data?.id === userId &&
      Date.now() - recentProfile.fetchedAt < PROFILE_REUSE_MS
    ) {
      return Promise.resolve(recentProfile.response);
    }
  }

  const request = fetchProfile();

  profileInFlight = request;

  const settle = () => {
    if (profileInFlight === request) profileInFlight = null;
  };

  request.then(settle, settle);
  return request;
};

/**
 * A profile write has landed: what was read before it is stale. Nothing
 * fetched earlier is reused, and a request still out is asked again.
 * Without this, finishing onboarding (`isOnboarded: true`) was undone a
 * moment later by the session gates re-reading the boot profile of the new
 * account, which sent the student back to onboarding (ORA-1003).
 */
const forgetProfileReads = (): void => {
  profileWriteSeq += 1;
  recentProfile = null;
};

/**
 * Call after a profile-affecting write made outside this service lands. This
 * prevents an older profile GET (or its reuse cache) from restoring pre-write
 * user data before the caller refreshes the profile.
 */
export const invalidateProfileReadsAfterWrite = (): void => {
  forgetProfileReads();
};

/** Test-only: forget the shared profile request. */
export const resetProfileRequestForTests = (): void => {
  profileInFlight = null;
  recentProfile = null;
  profileWriteSeq = 0;
};

/**
 * The profile as the server has it, without touching the store. The landing
 * loader reads it right after a login, before ProtectedRoute's own fetch has
 * run (that fetch stays the one that stores the profile, alongside the exam
 * metadata it loads with it).
 */
export const peekUserProfile = async (options?: {
  reuseRecent?: boolean;
}): Promise<User | undefined> => {
  try {
    const response = await requestProfile(options?.reuseRecent ?? false);
    return response.success ? response.data : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Asks the server whose session this is and lines the store up with it. A
 * different account than the store holds (another tab signed in, or a
 * persisted copy of someone else) resets every user-scoped slice to the new
 * identity; the route guard then loads its profile and exams (reusing this
 * response). The same account refreshes a profile already shown. A 401 signs
 * out, unless legacy tokens still wait for their exchange; anything else
 * (offline, 5xx) keeps the cached state for the next request to settle.
 */
const syncIdentityWithServer = async (reuseRecent: boolean): Promise<void> => {
  if (await holdForPendingSignOut()) return;
  let response: ProfileResponse;
  try {
    response = await requestProfile(reuseRecent);
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      !isLegacyExchangePending() &&
      store.getState().auth.isAuthenticated
    ) {
      store.dispatch(logout());
    }
    return;
  }

  const profile = response.success ? response.data : undefined;
  if (!profile?.id) return;

  const { auth, user } = store.getState();
  const shownProfileId = user.profile?.id ?? null;

  const isSameAccount =
    auth.isAuthenticated &&
    auth.userId === profile.id &&
    (shownProfileId === null || shownProfileId === profile.id);

  if (!isSameAccount) {
    changeSessionIdentity({
      userId: profile.id,
      impersonatedBy: readStoredImpersonation()?.impersonatedBy ?? null,
    });

    return;
  }

  if (shownProfileId) {
    store.dispatch(setAllMessageStates(profile.messageStates ?? []));
    store.dispatch(userReceived(profile));
  }
};

let isSessionIdentityChecked = false;
let sessionIdentityCheck: Promise<void> | null = null;
const sessionIdentityListeners = new Set<() => void>();

const markSessionIdentityChecked = (): void => {
  if (isSessionIdentityChecked) return;
  isSessionIdentityChecked = true;
  sessionIdentityListeners.forEach((listener) => listener());
};

/** True once this page load knows the session's account (see below). */
export const hasCheckedSessionIdentity = (): boolean =>
  isSessionIdentityChecked;

export const subscribeToSessionIdentityCheck = (
  listener: () => void
): (() => void) => {
  sessionIdentityListeners.add(listener);
  return () => sessionIdentityListeners.delete(listener);
};

/**
 * The boot's identity check: a rehydrated store is only a cached copy of
 * whoever was signed in, and the cookie may since have been revoked or
 * replaced. Runs once per page load (a sign-in in this page load counts) and
 * shares the profile request with the other boot callers.
 */
export const reconcileSessionIdentity = (): Promise<void> => {
  if (isSessionIdentityChecked) return Promise.resolve();

  sessionIdentityCheck ??= syncIdentityWithServer(true).finally(() => {
    sessionIdentityCheck = null;
    markSessionIdentityChecked();
  });

  return sessionIdentityCheck;
};

/** Test-only: forget this page load's identity check. */
export const resetSessionIdentityCheckForTests = (): void => {
  isSessionIdentityChecked = false;
  sessionIdentityCheck = null;
};

/**
 * What this tab does when another tab's session changed: it asks the server
 * who the shared cookie belongs to now and lines up with the answer, without
 * a reload. A sign-out signs this tab out only when the server answers 401
 * (an event any script on the origin could forge is not proof); a sign-in or
 * impersonation switch to another account resets to that account.
 */
export const handleSessionChangeFromAnotherTab = (
  event: SessionChangeEvent
): Promise<void> => {
  const { auth } = store.getState();

  if (event.type === 'signed-out') {
    return auth.isAuthenticated
      ? syncIdentityWithServer(false)
      : Promise.resolve();
  }

  if (auth.isAuthenticated && event.userId === auth.userId) {
    return Promise.resolve();
  }
  return syncIdentityWithServer(false);
};

/** Listens for other tabs' session changes. Returns the unsubscribe. */
export const startSessionSync = (): (() => void) =>
  subscribeToSessionChanges((event) => {
    void handleSessionChangeFromAnotherTab(event);
  });

export const getUserInfo = async (options?: {
  reuseRecent?: boolean;
}): Promise<User | undefined> => {
  try {
    store.dispatch(userLoading());

    const response = await requestProfile(options?.reuseRecent ?? false);

    if (!response.success || !response.data) {
      store.dispatch(userFailed(response.error || 'Failed to get user info'));

      console.error('Failed to get user info:', response.error);
      return undefined;
    }

    const user = response.data;
    store.dispatch(setAllMessageStates(user.messageStates ?? []));
    store.dispatch(userReceived(user));
    return user;
  } catch (error: unknown) {
    // Handle specific error cases as documented
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 401:
          console.error('Authentication error:', error.response.data.error);
          // The session is gone, unless this boot still holds legacy tokens
          // it could not trade yet (then the 401 is the missing cookie, and
          // signing out would delete the tokens the next boot retries with).
          if (!isLegacyExchangePending()) store.dispatch(logout());
          break;
        case 404:
          console.error('User not found:', error.response.data.error);
          break;
        case 500:
          console.error('Server error:', error.response.data.error);
          break;
        default:
          console.error('Failed to get user info:', error);
      }
    } else {
      console.error('Unexpected error:', error);
    }
    return undefined;
  }
};

export const updateCurrentUserInfo = async (
  userInfo: Partial<UpdateUserInfoRequest>
): Promise<User | undefined> => {
  try {
    store.dispatch(userLoading());

    const response = await axiosInstance.post<ApiResponse<User>>(
      '/auth/updateProfile',
      userInfo
    );

    if (!response.data.success || !response.data.data) {
      store.dispatch(
        userFailed(response.data.error || 'Failed to update user info')
      );

      throw new Error('Failed to update user info');
    }

    forgetProfileReads();

    const user = response.data.data;
    store.dispatch(setAllMessageStates(user.messageStates ?? []));
    store.dispatch(userUpdateReceived(user));
    return user;
  } catch (error) {
    console.error('Failed to update user info:', error);
    return undefined;
  }
};

export const updateTimezoneQuietly = async (
  timezone: string
): Promise<void> => {
  try {
    const response = await axiosInstance.post<ApiResponse<User>>(
      '/auth/updateProfile',
      { timezone }
    );

    if (!response.data.success || !response.data.data) {
      console.error('Failed to update timezone:', response.data.error);
      return;
    }

    forgetProfileReads();

    // The account may have switched (impersonation, re-login) while the
    // request was in flight; never merge a stale response into another
    // user's profile. Merge only the timezone so a snapshot captured before
    // a concurrent foreground update cannot clobber newer state.
    const updatedUser = response.data.data;
    const currentProfileId = store.getState().user.profile?.id;
    if (getActiveImpersonation() || updatedUser.id !== currentProfileId) {
      return;
    }

    store.dispatch(userUpdateReceived({ timezone: updatedUser.timezone }));
  } catch (error) {
    console.error('Failed to update timezone:', error);
  }
};

export const claimMarketingOfferKey = async (
  marketingOfferKey: string
): Promise<string | null> => {
  try {
    const response = await axiosInstance.post<
      ApiResponse<ClaimMarketingOfferResponse>
    >('/auth/claim-marketing-offer', {
      marketingOfferKey,
    });

    if (!response.data.success || !response.data.data) {
      return null;
    }

    const claimedMarketingOfferKey = response.data.data.marketingOfferKey;

    store.dispatch(
      userUpdateReceived({ marketingOfferKey: claimedMarketingOfferKey })
    );

    return claimedMarketingOfferKey;
  } catch (error) {
    console.error('Failed to claim marketing offer key:', error);
    return null;
  }
};

/**
 * Admin impersonation (ORA-987): the backend swaps this browser's session
 * cookie for one acting as the student, so every tab follows. The store
 * takes the student's identity through `sessionIdentityChanged`, which also
 * resets every user-scoped slice: nothing of the admin's account (plan,
 * school, study state) may linger under the student's session. Resolves to
 * the student's id, or undefined when the backend refused.
 */
export const impersonateUser = async (
  userId: string
): Promise<{ userId: string } | undefined> => {
  const adminId =
    store.getState().auth.userId ?? store.getState().user.profile?.id ?? null;

  try {
    const response = await axiosInstance.post<
      ApiResponse<{ userId?: string; impersonatedBy?: string }>
    >(IMPERSONATE_SESSION_PATH, { userId });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to impersonate user');
    }

    const impersonatedUserId = response.data.data?.userId ?? userId;
    const impersonatedBy = response.data.data?.impersonatedBy ?? adminId;

    if (impersonatedBy) {
      store.dispatch(impersonationStarted({ impersonatedBy }));
    }

    changeSessionIdentity({
      userId: impersonatedUserId,
      impersonatedBy: impersonatedBy ?? null,
    });

    broadcastSessionChange('impersonation-started', impersonatedUserId);

    return { userId: impersonatedUserId };
  } catch (error) {
    console.error('Failed to impersonate user:', error);
    return undefined;
  }
};

/**
 * Ends impersonation: the backend restores the admin's own session, and the
 * store drops the student's data the same way it took it on.
 */
export const stopImpersonating = async (): Promise<boolean> => {
  const impersonation = getActiveImpersonation();

  try {
    const response = await axiosInstance.post<ApiResponse<{ userId?: string }>>(
      STOP_IMPERSONATING_PATH
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to stop impersonating');
    }

    const adminId =
      response.data.data?.userId ?? impersonation?.impersonatedBy ?? null;

    store.dispatch(impersonationStopped());
    if (adminId) {
      changeSessionIdentity({ userId: adminId, impersonatedBy: null });
    }
    broadcastSessionChange('impersonation-stopped', adminId);
  } catch (error) {
    console.error('Failed to stop impersonating:', error);
    return false;
  }

  // Refresh user info
  await getUserInfo();
  await fetchExams();
  return true;
};

// Get Cohort Signup Count
export const getCohortSignupCount = async (cohort: string) => {
  const response = await axiosInstance.get(`/public/school-signups/${cohort}`);
  return response.data;
};

export const isValidCohort = async (cohort: string): Promise<boolean> => {
  const response = await axiosInstance.get(
    `/public/${encodeURIComponent(cohort)}/is-valid-cohort`
  );

  return response.data === true;
};

export const getCohortHasActiveDiscount = async (cohort: string) => {
  const response = await axiosInstance.get(`/public/cohort-discount/${cohort}`);
  return response.data?.hasDiscount === true;
};
